(function () {
  if (customElements.get('daniel-product-personalizer')) return;

  var ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'svg'];
  var ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/svg+xml'
  ];

  function extensionOf(file) {
    var parts = String(file && file.name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  function isAllowedFile(file) {
    var extension = extensionOf(file);
    var mime = String(file && file.type || '').toLowerCase();
    return ALLOWED_EXTENSIONS.indexOf(extension) !== -1 && (!mime || ALLOWED_MIME_TYPES.indexOf(mime) !== -1);
  }

  function isHeic(file) {
    var extension = extensionOf(file);
    var mime = String(file && file.type || '').toLowerCase();
    return extension === 'heic' || extension === 'heif' || mime === 'image/heic' || mime === 'image/heif';
  }

  function createGroupId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'dp-' + window.crypto.randomUUID();
    }
    return 'dp-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function rootRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/$/, '') + path;
  }

  async function readHeicDimensions(file) {
    var buffer = await file.arrayBuffer();
    var bytes = new Uint8Array(buffer);
    var view = new DataView(buffer);
    for (var index = 0; index + 16 < bytes.length; index += 1) {
      if (bytes[index] !== 105 || bytes[index + 1] !== 115 || bytes[index + 2] !== 112 || bytes[index + 3] !== 101) continue;
      var width = view.getUint32(index + 8, false);
      var height = view.getUint32(index + 12, false);
      if (width > 0 && height > 0 && width < 100000 && height < 100000) {
        return { width: width, height: height };
      }
    }
    throw new Error('heic-dimensions-unavailable');
  }

  async function readBrowserImageDimensions(file) {
    if (typeof window.createImageBitmap === 'function') {
      try {
        var bitmap = await window.createImageBitmap(file);
        var dimensions = { width: bitmap.width, height: bitmap.height };
        if (typeof bitmap.close === 'function') bitmap.close();
        if (dimensions.width && dimensions.height) return dimensions;
      } catch (error) {
        // The Image fallback covers browser-decodable formats not handled above.
      }
    }

    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var image = new Image();
      image.onload = function () {
        var dimensions = { width: image.naturalWidth, height: image.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(dimensions);
      };
      image.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('image-dimensions-unavailable'));
      };
      image.src = url;
    });
  }

  async function readDimensions(file) {
    if (isHeic(file)) {
      try {
        return await readHeicDimensions(file);
      } catch (error) {
        return readBrowserImageDimensions(file);
      }
    }
    return readBrowserImageDimensions(file);
  }

  async function responseJson(response) {
    var text = await response.text();
    var parsed;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      throw new Error('invalid-json-response');
    }
    if (!response.ok || parsed.status) {
      var message = parsed.description || parsed.message || 'shopify-request-failed';
      var requestError = new Error(message);
      requestError.shopifyResponse = parsed;
      throw requestError;
    }
    return parsed;
  }

  class DanielProductPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.setAttribute('data-engine-state', 'initializing');
      this.mode = this.dataset.mode || '';
      this.form = document.getElementById(this.dataset.formId || '');
      this.nameInput = this.querySelector('[data-daniel-name]');
      this.fileInput = this.querySelector('[data-daniel-image]');
      this.fileRequired = Boolean(this.fileInput && this.fileInput.dataset.required === 'true');
      this.dropzone = this.querySelector('[data-daniel-dropzone]');
      this.fileTriggers = this.querySelectorAll('[data-daniel-file-trigger]');
      this.fileRemove = this.querySelector('[data-daniel-file-remove]');
      this.nameError = this.querySelector('[data-daniel-name-error]');
      this.fileError = this.querySelector('[data-daniel-image-error]');
      this.globalError = this.querySelector('[data-daniel-global-error]');
      this.fileStatus = this.querySelector('[data-daniel-file-status]');
      this.previewWrap = this.querySelector('[data-daniel-preview-wrap]');
      this.previewImage = this.querySelector('[data-daniel-preview]');
      this.groupInput = this.querySelector('[data-daniel-group]');
      this.hasImageInput = this.querySelector('[data-daniel-has-image]');
      this.paidImageInput = this.querySelector('[data-daniel-paid-image]');
      this.nameMirrorInput = this.querySelector('[data-daniel-name-mirror]');
      this.validationToken = 0;
      this.fileState = 'empty';
      this.validatedFile = null;
      this.fileValidationPromise = null;
      this.previewUrl = '';
      this.transactionRunning = false;

      if (!this.form) {
        this.setAttribute('data-engine-state', 'error');
        this.showGlobalError(this.dataset.addError || 'Personalization unavailable.');
        return;
      }

      this.form.enctype = 'multipart/form-data';
      this.form.setAttribute('data-daniel-personalization-ready', 'true');
      this.setAttribute('data-engine-state', 'ready');
      this.boundSubmit = this.handleSubmit.bind(this);
      this.boundNameInput = this.handleNameInput.bind(this);
      this.boundFileChange = this.handleFileChange.bind(this);
      this.boundDragOver = this.handleDragOver.bind(this);
      this.boundDragLeave = this.handleDragLeave.bind(this);
      this.boundDrop = this.handleDrop.bind(this);
      this.boundRemove = this.clearSelectedFile.bind(this);
      this.form.addEventListener('submit', this.boundSubmit, true);

      if (this.nameInput) this.nameInput.addEventListener('input', this.boundNameInput);
      if (this.fileInput) this.fileInput.addEventListener('change', this.boundFileChange);
      this.fileTriggers.forEach(function (trigger) {
        trigger.addEventListener('click', this.openFilePicker.bind(this));
      }, this);
      if (this.fileRemove) this.fileRemove.addEventListener('click', this.boundRemove);
      if (this.dropzone) {
        this.dropzone.addEventListener('dragover', this.boundDragOver);
        this.dropzone.addEventListener('dragleave', this.boundDragLeave);
        this.dropzone.addEventListener('drop', this.boundDrop);
      }

      this.emitState();
      window.requestAnimationFrame(this.emitState.bind(this));
    }

    disconnectedCallback() {
      if (this.form && this.boundSubmit) this.form.removeEventListener('submit', this.boundSubmit, true);
      if (this.nameInput && this.boundNameInput) this.nameInput.removeEventListener('input', this.boundNameInput);
      if (this.fileInput && this.boundFileChange) this.fileInput.removeEventListener('change', this.boundFileChange);
      if (this.fileRemove && this.boundRemove) this.fileRemove.removeEventListener('click', this.boundRemove);
      if (this.dropzone) {
        this.dropzone.removeEventListener('dragover', this.boundDragOver);
        this.dropzone.removeEventListener('dragleave', this.boundDragLeave);
        this.dropzone.removeEventListener('drop', this.boundDrop);
      }
      this.revokePreview();
    }

    text(key, fallback) {
      return this.dataset[key] || fallback || '';
    }

    openFilePicker(event) {
      event.preventDefault();
      if (!this.transactionRunning && this.fileInput) this.fileInput.click();
    }

    handleDragOver(event) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      if (this.dropzone) this.dropzone.classList.add('is-dragging');
    }

    handleDragLeave(event) {
      if (event.currentTarget === this.dropzone && this.dropzone) this.dropzone.classList.remove('is-dragging');
    }

    handleDrop(event) {
      event.preventDefault();
      if (this.dropzone) this.dropzone.classList.remove('is-dragging');
      if (!this.fileInput || !event.dataTransfer || !event.dataTransfer.files.length) return;
      var transfer = new DataTransfer();
      transfer.items.add(event.dataTransfer.files[0]);
      this.fileInput.files = transfer.files;
      this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleNameInput() {
      this.groupInput.value = '';
      this.clearFieldError(this.nameInput, this.nameError);
      this.clearGlobalError();
      this.nameMirrorInput.value = this.nameInput.value.trim();
      this.emitState();
    }

    handleFileChange() {
      this.groupInput.value = '';
      this.clearFieldError(this.dropzone, this.fileError);
      this.clearGlobalError();
      this.fileState = this.currentFile() ? 'pending' : 'empty';
      this.validatedFile = null;
      this.fileValidationPromise = this.validateSelectedFile();
      this.emitState();
    }

    clearSelectedFile(event) {
      if (event) event.preventDefault();
      if (!this.fileInput || this.transactionRunning) return;
      this.fileInput.value = '';
      this.handleFileChange();
      if (this.dropzone) this.dropzone.focus();
    }

    blockSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleSubmit(event) {
      this.clearGlobalError();
      if (this.transactionRunning) {
        this.blockSubmit(event);
        return;
      }

      var nameIsValid = this.validateName();
      var file = this.currentFile();
      var fileIsRequired = this.fileRequired;

      if (fileIsRequired && !file) {
        this.showFieldError(this.dropzone, this.fileError, this.text('imageRequiredError'));
        this.blockSubmit(event);
        this.focusFirstInvalid();
        this.emitState();
        return;
      }

      if (!nameIsValid) {
        this.blockSubmit(event);
        this.focusFirstInvalid();
        this.emitState();
        return;
      }

      if (file && (this.fileState === 'pending' || this.validatedFile !== file)) {
        var submitter = event.submitter || null;
        this.blockSubmit(event);
        this.resumeAfterFileValidation(submitter);
        return;
      }

      if (file && this.fileState !== 'valid') {
        this.blockSubmit(event);
        this.focusFirstInvalid();
        this.emitState();
        return;
      }

      if (file && this.mode === 'name_image_optional_paid' && this.dataset.feeReady !== 'true') {
        this.blockSubmit(event);
        this.showGlobalError(this.text('feeError'));
        if (this.dropzone) this.dropzone.focus();
        this.emitState();
        return;
      }

      this.prepareProperties(file);
      if (file && this.mode === 'name_image_optional_paid') {
        this.blockSubmit(event);
        this.submitPaidPersonalization(event.submitter || null);
        return;
      }
      this.emitState(true);
    }

    async resumeAfterFileValidation(submitter) {
      var valid = await (this.fileValidationPromise || this.validateSelectedFile());
      if (!valid) {
        this.focusFirstInvalid();
        this.emitState();
        return;
      }
      if (submitter && submitter.form === this.form) {
        this.form.requestSubmit(submitter);
      } else {
        this.form.requestSubmit();
      }
    }

    validateName() {
      if (!this.nameInput) return true;
      var value = this.nameInput.value.trim();
      this.nameMirrorInput.value = value;
      if (this.nameInput.required && !value) {
        this.showFieldError(this.nameInput, this.nameError, this.text('nameError'));
        return false;
      }
      this.clearFieldError(this.nameInput, this.nameError);
      return true;
    }

    currentFile() {
      return this.fileInput && this.fileInput.files && this.fileInput.files.length ? this.fileInput.files[0] : null;
    }

    async validateSelectedFile() {
      var file = this.currentFile();
      var token = ++this.validationToken;
      this.revokePreview();
      if (!file) {
        this.fileState = 'empty';
        this.validatedFile = null;
        this.hasImageInput.value = 'false';
        this.paidImageInput.value = 'false';
        this.setFileStatus('');
        this.emitState();
        return !this.fileRequired;
      }

      this.fileState = 'pending';
      this.setFileStatus(this.text('checkingImage'));
      var maximum = Number(this.dataset.maxFileSize || 20971520);
      if (file.size === 0) return this.failFile(token, this.text('fileEmptyError'));
      if (file.size > maximum) return this.failFile(token, this.text('fileSizeError'));
      if (!isAllowedFile(file)) return this.failFile(token, this.text('fileTypeError'));

      try {
        var dimensions = await readDimensions(file);
        if (token !== this.validationToken || file !== this.currentFile()) return false;
        var minimumWidth = Number(this.dataset.minWidth || 200);
        var minimumHeight = Number(this.dataset.minHeight || 200);
        if (dimensions.width < minimumWidth || dimensions.height < minimumHeight) {
          return this.failFile(token, this.text('fileDimensionsError'));
        }
        this.fileState = 'valid';
        this.validatedFile = file;
        this.hasImageInput.value = 'true';
        this.paidImageInput.value = this.mode === 'name_image_optional_paid' ? 'true' : 'false';
        this.clearFieldError(this.dropzone, this.fileError);
        this.setFileStatus(this.text('imageReady') + ': ' + file.name + ' (' + dimensions.width + ' × ' + dimensions.height + ' px)');
        this.showPreview(file);
        this.emitState();
        return true;
      } catch (error) {
        return this.failFile(token, this.text('fileReadError'));
      }
    }

    failFile(token, message) {
      if (token !== this.validationToken) return false;
      this.fileState = 'invalid';
      this.validatedFile = this.currentFile();
      this.hasImageInput.value = 'false';
      this.paidImageInput.value = 'false';
      this.setFileStatus('');
      this.showFieldError(this.dropzone, this.fileError, message);
      this.emitState();
      return false;
    }

    prepareProperties(file) {
      this.nameMirrorInput.value = this.nameInput ? this.nameInput.value.trim() : '';
      this.hasImageInput.value = file ? 'true' : 'false';
      this.paidImageInput.value = file && this.mode === 'name_image_optional_paid' ? 'true' : 'false';
      this.groupInput.value = createGroupId();
    }

    quantity() {
      var input = this.form.querySelector('[name="quantity"]');
      var value = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    async addFee(group, quantity) {
      var feeVariantId = this.dataset.feeVariantId;
      var payload = {
        items: [{
          id: Number(feeVariantId),
          quantity: quantity,
          properties: {
            _daniel_personalization_version: this.dataset.version || 'v1',
            _daniel_personalization_role: 'fee',
            _daniel_personalization_group: group,
            _daniel_personalization_parent_variant: String(this.form.querySelector('[name="id"]').value)
          }
        }]
      };
      var response = await fetch(window.routes && window.routes.cart_add_url || rootRoute('/cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      return responseJson(response);
    }

    async addParent() {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var formData = new FormData(this.form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map(function (section) { return section.id; }));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
      var response = await fetch(window.routes && window.routes.cart_add_url || rootRoute('/cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      return responseJson(response);
    }

    async cartState() {
      var response = await fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : rootRoute('/cart.js'), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      return responseJson(response);
    }

    groupItems(cart, group) {
      return (cart.items || []).filter(function (item) {
        return item.properties && item.properties._daniel_personalization_group === group;
      });
    }

    async verifyGroup(group, quantity, expectParent) {
      for (var attempt = 0; attempt < 3; attempt += 1) {
        try {
          var cart = await this.cartState();
          var items = this.groupItems(cart, group);
          var fees = items.filter(function (item) { return item.properties._daniel_personalization_role === 'fee'; });
          var parents = items.filter(function (item) { return item.properties._daniel_personalization_role === 'parent'; });
          if (fees.length === 1 && fees[0].quantity === quantity && (!expectParent || parents.length === 1 && parents[0].quantity === quantity)) {
            return { valid: true, cart: cart, fee: fees[0], parent: parents[0] || null };
          }
        } catch (error) {
          if (attempt === 2) throw error;
        }
        await new Promise(function (resolve) { window.setTimeout(resolve, 180 * (attempt + 1)); });
      }
      return { valid: false };
    }

    async removeGroup(group) {
      for (var attempt = 0; attempt < 3; attempt += 1) {
        try {
          var cart = await this.cartState();
          var items = this.groupItems(cart, group);
          if (!items.length) return true;
          var updates = {};
          items.forEach(function (item) { updates[item.key] = 0; });
          var response = await fetch(window.routes && window.routes.cart_update_url || rootRoute('/cart/update.js'), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
          await responseJson(response);
          var check = await this.cartState();
          if (!this.groupItems(check, group).length) return true;
        } catch (error) {
          // Retried below so a transient request cannot leave an orphan fee.
        }
        await new Promise(function (resolve) { window.setTimeout(resolve, 220 * (attempt + 1)); });
      }
      return false;
    }

    setTransactionLoading(loading, submitter) {
      this.transactionRunning = loading;
      this.setAttribute('data-transaction-state', loading ? 'loading' : 'idle');
      var button = submitter || this.form.querySelector('[type="submit"]');
      if (!button) return;
      if (loading) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      } else {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      }
      var spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }

    async submitPaidPersonalization(submitter) {
      var group = this.groupInput.value;
      var quantity = this.quantity();
      var feeAdded = false;
      var parentAdded = false;
      this.setTransactionLoading(true, submitter);

      try {
        await this.addFee(group, quantity);
        feeAdded = true;
        var feeCheck = await this.verifyGroup(group, quantity, false);
        if (!feeCheck.valid) throw new Error('fee-verification-failed');

        var parentResponse = await this.addParent();
        parentAdded = true;
        var completeCheck = await this.verifyGroup(group, quantity, true);
        if (!completeCheck.valid) throw new Error('group-verification-failed');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'daniel-personalizer',
            productVariantId: this.form.querySelector('[name="id"]').value,
            cartData: parentResponse
          });
        }

        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.renderContents === 'function' && parentResponse.sections) {
          cart.renderContents(parentResponse);
        } else {
          window.location.assign(window.routes && window.routes.cart_url || rootRoute('/cart'));
          return;
        }
        this.resetAfterSuccess();
      } catch (error) {
        var rolledBack = true;
        if (feeAdded || parentAdded) rolledBack = await this.removeGroup(group);
        if (!rolledBack && window.DanielPersonalizationCart && typeof window.DanielPersonalizationCart.reconcile === 'function') {
          window.DanielPersonalizationCart.reconcile(true);
        }
        this.showGlobalError(rolledBack ? this.text('addError') : this.text('rollbackError'));
      } finally {
        this.setTransactionLoading(false, submitter);
        this.emitState();
      }
    }

    resetAfterSuccess() {
      if (this.nameInput) this.nameInput.value = '';
      if (this.fileInput) this.fileInput.value = '';
      this.groupInput.value = '';
      this.nameMirrorInput.value = '';
      this.hasImageInput.value = 'false';
      this.paidImageInput.value = 'false';
      this.fileState = 'empty';
      this.validatedFile = null;
      this.revokePreview();
      this.clearGlobalError();
    }

    showPreview(file) {
      if (!this.previewWrap) return;
      this.previewWrap.hidden = false;
      if (this.dropzone) this.dropzone.hidden = true;
      if (!this.previewImage || isHeic(file)) {
        if (this.previewImage) this.previewImage.hidden = true;
        return;
      }
      this.previewUrl = URL.createObjectURL(file);
      this.previewImage.src = this.previewUrl;
      this.previewImage.alt = this.text('previewAlt');
      this.previewImage.hidden = false;
    }

    revokePreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = '';
      if (this.previewImage) {
        this.previewImage.removeAttribute('src');
        this.previewImage.hidden = true;
      }
      if (this.previewWrap) this.previewWrap.hidden = true;
      if (this.dropzone) this.dropzone.hidden = false;
    }

    setFileStatus(message) {
      if (!this.fileStatus) return;
      this.fileStatus.textContent = message;
    }

    showFieldError(input, output, message) {
      if (input) input.setAttribute('aria-invalid', 'true');
      if (output) {
        output.textContent = message;
        output.hidden = false;
      }
    }

    clearFieldError(input, output) {
      if (input) input.removeAttribute('aria-invalid');
      if (output) {
        output.textContent = '';
        output.hidden = true;
      }
    }

    showGlobalError(message) {
      if (!this.globalError) return;
      this.globalError.textContent = message;
      this.globalError.hidden = false;
    }

    clearGlobalError() {
      if (!this.globalError) return;
      this.globalError.textContent = '';
      this.globalError.hidden = true;
    }

    focusFirstInvalid() {
      var invalid = this.querySelector('[aria-invalid="true"]');
      if (invalid) invalid.focus();
    }

    isComplete() {
      if (this.nameInput && this.nameInput.required && !this.nameInput.value.trim()) return false;
      var file = this.currentFile();
      if (this.fileInput && this.fileRequired && !file) return false;
      if (file && this.fileState !== 'valid') return false;
      if (file && this.mode === 'name_image_optional_paid' && this.dataset.feeReady !== 'true') return false;
      return true;
    }

    emitState(forcedValid) {
      var valid = typeof forcedValid === 'boolean' ? forcedValid : this.isComplete();
      this.setAttribute('data-complete', valid ? 'true' : 'false');
      this.dispatchEvent(new CustomEvent('daniel:personalization-state', {
        bubbles: true,
        detail: {
          valid: valid,
          mode: this.mode,
          completeLabel: this.text('completeLabel')
        }
      }));
    }
  }

  customElements.define('daniel-product-personalizer', DanielProductPersonalizer);
})();
